/**
 * Group-level reporting hooks.
 *
 * v2 (demo polish): every detail hook now accepts an optional `businessId`
 * param. When passed, the report is scoped to that single business. When
 * omitted/null, the report is consolidated across the whole group.
 *
 * Endpoints:
 *   GET /group-reports/snapshot
 *   GET /group-reports/pnl?from=&to=&businessId=
 *   GET /group-reports/cash-flow?from=&to=&businessId=
 *   GET /group-reports/trial-balance?asOf=&businessId=
 *
 * All money in INTEGER MINOR UNITS (pesewas).
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";

interface UnwrappedResponse<T> {
  success: boolean;
  data: T;
}

async function getJson<T>(url: string, params: object = {}): Promise<T> {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== "") clean[k] = String(v);
  }
  const res = await api.get<UnwrappedResponse<T> | T>(url, { params: clean });
  const body = res.data as unknown;
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as object) &&
    "success" in (body as object)
  ) {
    return (body as UnwrappedResponse<T>).data;
  }
  return body as T;
}

// ─── 1. Snapshot ──────────────────────────────────────────────────

export interface GroupSnapshotResponse {
  businessCount: number;
  totals: {
    revenue: number;
    expenses: number;
    netProfit: number;
  };
  topContributor?: {
    businessId: string;
    businessName: string;
    revenue: number;
    netProfit: number;
  };
  perBusiness?: Array<{
    businessId: string;
    businessName: string;
    revenue: number;
    expenses: number;
    netProfit: number;
  }>;
  errors?: Array<{ businessId: string; reason: string }>;
}

export function useGroupSnapshot() {
  return useQuery<GroupSnapshotResponse>({
    queryKey: ["group-reports", "snapshot"],
    queryFn: () => getJson<GroupSnapshotResponse>("/group-reports/snapshot"),
    staleTime: 60 * 1000,
  });
}

// ─── 2. Group P&L ─────────────────────────────────────────────────

export interface AccountLine {
  accountId?: string;
  code: string;
  name: string;
  amountMinor: number;
}

export interface PnLSection {
  total: number;
  accounts?: AccountLine[];
}

export interface GroupPnLResponse {
  from: string;
  to: string;
  currency: string;
  businessId?: string | null;
  businessName?: string | null;
  revenue: PnLSection;
  expenses: PnLSection;
  cogs?: PnLSection;
  grossProfit?: number;
  netProfit: number;
  perBusiness?: Array<{
    businessId: string;
    businessName: string;
    revenueMinor: number;
    expensesMinor: number;
    netProfitMinor: number;
  }>;
}

export function useGroupPnL(
  query: { from?: string; to?: string; businessId?: string | null } = {},
) {
  return useQuery<GroupPnLResponse>({
    queryKey: ["group-reports", "pnl", query],
    queryFn: () => getJson<GroupPnLResponse>("/group-reports/pnl", query),
    staleTime: 60 * 1000,
  });
}

// ─── 3. Group Cash Flow ───────────────────────────────────────────

export interface CashFlowActivity {
  total: number;
  lines?: Array<{
    label: string;
    amountMinor: number;
  }>;
}

export interface GroupCashFlowResponse {
  from: string;
  to: string;
  currency: string;
  businessId?: string | null;
  businessName?: string | null;
  operating: CashFlowActivity;
  investing?: CashFlowActivity;
  financing?: CashFlowActivity;
  netChange: number;
  openingBalance?: number;
  closingBalance?: number;
  perBusiness?: Array<{
    businessId: string;
    businessName: string;
    operatingMinor: number;
    investingMinor?: number;
    financingMinor?: number;
    netChangeMinor: number;
  }>;
}

export function useGroupCashFlow(
  query: { from?: string; to?: string; businessId?: string | null } = {},
) {
  return useQuery<GroupCashFlowResponse>({
    queryKey: ["group-reports", "cash-flow", query],
    queryFn: () =>
      getJson<GroupCashFlowResponse>("/group-reports/cash-flow", query),
    staleTime: 60 * 1000,
  });
}

// ─── 4. Group Trial Balance ───────────────────────────────────────

export interface TrialBalanceRow {
  accountId?: string;
  code: string;
  name: string;
  type: string;
  debitMinor: number;
  creditMinor: number;
}

export interface GroupTrialBalanceResponse {
  asOf: string;
  currency: string;
  businessId?: string | null;
  businessName?: string | null;
  rows: TrialBalanceRow[];
  totalDebitMinor: number;
  totalCreditMinor: number;
  balanced: boolean;
}

export function useGroupTrialBalance(
  query: { asOf?: string; businessId?: string | null } = {},
) {
  return useQuery<GroupTrialBalanceResponse>({
    queryKey: ["group-reports", "trial-balance", query],
    queryFn: () =>
      getJson<GroupTrialBalanceResponse>("/group-reports/trial-balance", query),
    staleTime: 60 * 1000,
  });
}

// ─── 5. Group performance (business vs business) ──────────────────

export type CompareWith = "previous_period" | "previous_month" | "previous_year";

export interface PerformanceRow {
  businessId: string;
  businessName: string;
  slug: string;
  industry: string | null;
  currency: string;
  converted: boolean;
  rate: number | null;
  own: {
    revenueMinor: number;
    grossProfitMinor: number;
    netProfitMinor: number;
    expensesMinor: number;
    orders: number;
    cashInMinor: number;
    avgSaleMinor: number | null;
  };
  revenueMinor: number | null;
  costOfSalesMinor: number | null;
  grossProfitMinor: number | null;
  expensesMinor: number | null;
  netProfitMinor: number | null;
  cashInMinor: number | null;
  avgSaleMinor: number | null;
  orders: number;
  grossMarginPct: number | null;
  netMarginPct: number | null;
  previous: { revenueMinor: number | null; netProfitMinor: number | null; grossProfitMinor: number | null };
  revenueGrowthPct: number | null;
  profitGrowthPct: number | null;
  revenueSharePct: number | null;
  profitSharePct: number | null;
  rank: number | null;
  series: { key: string; revenueMinor: number | null; orders: number }[];
}

export interface Highlight {
  businessId: string;
  businessName: string;
  value: number | null;
  reason: string;
}

export interface GroupPerformanceResponse {
  period: { from: string; to: string; days: number; bucket: "day" | "week" | "month" };
  previous: { from: string; to: string; label: string };
  compare: CompareWith;
  currency: string;
  ratesStale: boolean;
  businessCount: number;
  totals: {
    revenueMinor: number;
    grossProfitMinor: number;
    expensesMinor: number;
    netProfitMinor: number;
    cashInMinor: number;
    orders: number;
    avgSaleMinor: number | null;
    grossMarginPct: number | null;
    netMarginPct: number | null;
    previousRevenueMinor: number;
    previousNetProfitMinor: number;
    revenueGrowthPct: number | null;
    profitGrowthPct: number | null;
  };
  businesses: PerformanceRow[];
  highlights: {
    bestPerforming: Highlight | null;
    topRevenue: Highlight | null;
    bestMargin: Highlight | null;
    fastestGrowing: Highlight | null;
    needsAttention: Highlight | null;
  };
  incomplete: boolean;
  failedBusinesses: { businessId: string; businessName: string; reason: string }[];
  unconverted: { businessId: string; businessName: string; currency: string }[];
}

export function useGroupPerformance(query: { from?: string; to?: string; compare?: CompareWith; currency?: string }) {
  return useQuery<GroupPerformanceResponse>({
    queryKey: ["group-reports", "performance", query],
    queryFn: () => getJson<GroupPerformanceResponse>("/group-reports/performance", query),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

// ─── 6. Group expenses & payroll (All businesses mode) ────────────

export interface GroupExpensesResponse {
  period: { from: string; to: string };
  currency: string;
  totals: { approvedMinor: number; pendingMinor: number; count: number };
  categories: { category: string; amountMinor: number; sharePct: number | null }[];
  businesses: {
    businessId: string;
    businessName: string;
    slug: string;
    currency: string;
    converted: boolean;
    approvedMinor: number | null;
    pendingMinor: number | null;
    count: number;
    sharePct: number | null;
    own: { approvedMinor: number; pendingMinor: number };
    categories: { category: string; ownMinor: number; amountMinor: number | null }[];
  }[];
  unconverted: { businessId: string; businessName: string; currency: string }[];
}

export function useGroupExpenses(query: { from?: string; to?: string; currency?: string }) {
  return useQuery<GroupExpensesResponse>({
    queryKey: ["group-reports", "expenses", query],
    queryFn: () => getJson<GroupExpensesResponse>("/group-reports/expenses", query),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export interface GroupPayrollResponse {
  period: { from: string; to: string };
  currency: string;
  totals: { employerCostMinor: number; grossMinor: number; netMinor: number; unpaidMinor: number; employees: number };
  months: { period: string; employerCostMinor: number }[];
  businesses: {
    businessId: string;
    businessName: string;
    slug: string;
    currency: string;
    converted: boolean;
    employees: number;
    grossMinor: number;
    netMinor: number;
    employerCostMinor: number;
    unpaidMinor: number;
    sharePct: number | null;
    months: { period: string; employerCostMinor: number }[];
  }[];
  unconverted: { businessId: string; businessName: string; currency: string }[];
}

export function useGroupPayroll(query: { from?: string; to?: string; currency?: string }) {
  return useQuery<GroupPayrollResponse>({
    queryKey: ["group-reports", "payroll", query],
    queryFn: () => getJson<GroupPayrollResponse>("/group-reports/payroll", query),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
